import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  AgentStreamEventScheme,
  type AgentStreamEvent,
  type ChatSession,
  type ChatSessionMessage,
} from "@KassaroAB/shared/types/chatSession";
import { auth } from "../config/firebase";
import { STREAM_API_BASE_URL } from "../constants/api";
import { ApiError } from "../utils/api";

export type AgentProgressStep = {
  tool: string;
  phase: "started" | "done";
};

const parseNdjsonBuffer = (
  buffer: string,
): { events: AgentStreamEvent[]; remainder: string } => {
  const lines = buffer.split("\n");
  const remainder = lines.pop() ?? "";
  const events = lines
    .filter((line) => line.trim().length > 0)
    .map((line) => AgentStreamEventScheme.parse(JSON.parse(line)));
  return { events, remainder };
};

const streamFormDataRequest = async (
  endpoint: string,
  body: FormData,
): Promise<Response> => {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  const token = await auth.currentUser.getIdToken();

  return fetch(`${STREAM_API_BASE_URL}/${endpoint.replace(/^\//, "")}`, {
    method: "POST",
    headers: {
      Accept: "application/x-ndjson",
      Authorization: `Bearer ${token}`,
    },
    body,
  }).catch((error: unknown) => {
    if (error instanceof TypeError) {
      throw new Error("Could not reach the Kassaro API. Check your connection and try again.");
    }
    throw error instanceof Error ? error : new Error("Could not reach the Kassaro API.");
  });
};

export async function* readAgentStreamEvents(
  response: Response,
): AsyncGenerator<AgentStreamEvent> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new ApiError(response.status, errorData);
  }

  const reader = response.body?.getReader();
  if (reader === undefined) {
    throw new Error("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const parsed = parseNdjsonBuffer(buffer);
    buffer = parsed.remainder;
    for (const event of parsed.events) {
      yield event;
    }
  }

  if (buffer.trim().length > 0) {
    yield AgentStreamEventScheme.parse(JSON.parse(buffer));
  }
}

const buildMessageFormData = (message: string, files?: File[]): FormData => {
  const body = new FormData();
  body.append("message", message);
  (files ?? []).forEach((file) => body.append("file", file));
  return body;
};

export const formatStreamChatError = (error: unknown): Error => {
  if (error instanceof ApiError) {
    if (error.message === "Validation error") {
      return new Error("Write a message or attach at least one file before sending.");
    }
    if (error.message.includes("array too long")) {
      return new Error(
        "The assistant tried to save too much data in one step. Please send your message again or start a new chat.",
      );
    }
    if (error.message.includes("Chat session has no OpenAI conversation")) {
      return new Error(
        "This chat session could not connect to the AI assistant. Please send your message again or start a new chat.",
      );
    }
    if (error.message.includes("Could not reach the AI assistant")) {
      return new Error(error.message);
    }
    return error;
  }
  if (error instanceof Error) {
    if (error.message.includes("array too long")) {
      return new Error(
        "The assistant tried to save too much data in one step. Please send your message again or start a new chat.",
      );
    }
    return error;
  }
  return new Error("Stream failed");
};

export type StreamChatHandlers = {
  onProgress?: (step: AgentProgressStep) => void;
  onUserMessage?: (message: ChatSessionMessage) => void;
  onAssistantMessage?: (message: ChatSessionMessage) => void;
  onSession?: (session: ChatSession) => void;
  onError?: (message: string) => void;
};

const consumeAgentStream = async (
  response: Response,
  handlers: StreamChatHandlers,
): Promise<void> => {
  for await (const event of readAgentStreamEvents(response)) {
    if (event.type === "progress") {
      handlers.onProgress?.({ tool: event.tool, phase: event.phase });
    }
    if (event.type === "user_message") {
      handlers.onUserMessage?.(event.message);
    }
    if (event.type === "assistant_message") {
      handlers.onAssistantMessage?.(event.message);
    }
    if (event.type === "session") {
      handlers.onSession?.(event.session);
    }
    if (event.type === "error") {
      handlers.onError?.(event.message);
    }
  }
};

export const streamCreateStoreChatSession = async (
  storeId: StoreId,
  message: string,
  files: File[] | undefined,
  handlers: StreamChatHandlers,
): Promise<void> => {
  const body = buildMessageFormData(message, files);
  const response = await streamFormDataRequest(
    `stores/${storeId}/chatSessions`,
    body,
  );
  await consumeAgentStream(response, handlers);
};

export const streamPostStoreChatSessionMessage = async (
  storeId: StoreId,
  chatSessionId: string,
  message: string,
  files: File[] | undefined,
  handlers: StreamChatHandlers,
): Promise<void> => {
  const body = buildMessageFormData(message, files);
  const response = await streamFormDataRequest(
    `stores/${storeId}/chatSessions/${chatSessionId}/messages`,
    body,
  );
  await consumeAgentStream(response, handlers);
};
